"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import Icons from "@/lib/icons";
import { Link, usePathname } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import BorderWrapper from "@/components/ui/border-wrapper";
import Socials from "@/components/common/socials";
import Image from "next/image";
import { motion } from "motion/react";
import AnimatedCard from "../ui/animated-card";
import GetInTouch from "../modals/get-in-touch";
import parse from "html-react-parser";

const Footer = () => {
  const [isGetInTouchOpen, setIsGetInTouchOpen] = useState(false);
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("footer");
  const tNav = useTranslations("nav");
  const tagline = t.raw("tagline");
  const isActive = (href: string) => pathname === href;
  const footerLinkClass = "text-foreground hover:text-[#6b52b5] transition-all duration-300";
  return (
    <footer className="site-footer relative pt-[5rem] md:pt-[10rem]">
      <div className="container space-y-[20px] relative">
        {/* BACKGROUND SHAPE */}
        <motion.div
          className="absolute -top-[3rem] md:-top-[calc(10rem+50px)] right-0 md:-right-[29.25rem] lg:-right-[23rem] -z-[1]"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            duration: 1.2,
            ease: "easeOut",
            delay: 0.3,
          }}
        >
          <Image
            src="/assets/backgrounds/footer-bg.svg"
            alt="Footer background graphic"
            aria-hidden="true"
            width={1920}
            height={1080}
            className="w-[85rem] max-w-[1046px] lg:max-w-[1206px] h-auto hidden md:block object-contain"
          />
          <Image
            src="/assets/backgrounds/footer-bg-mobile.svg"
            alt="Footer background graphic"
            aria-hidden="true"
            width={1920}
            height={1080}
            className="w-[85rem] max-w-[100vw] lg:max-w-[60vw] h-auto block md:hidden"
          />
        </motion.div>

        <motion.div
          className="space-y-[20px] pb-[30px] lg:pb-0 relative z-[1]"
          initial={{ y: 30 }}
          animate={{ y: 0 }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 0.5,
          }}
        >
          <h2 className="text-[40px] sm:text-[56px] lg:text-[90px] xl:text-[120px] font-semibold text-white leading-[1.15]">
            {t("startProject")}
          </h2>
          <div className="inline-flex items-center gap-[10px]">
            <Button
              variant="theme"
              className="h-[40px] lg:h-auto text-[14px] lg:text-[16px] font-light cursor-pointer"
              onClick={() => setIsGetInTouchOpen(true)}
            >
              {t("getInTouch")}
            </Button>
            <Button
              variant="secondary"
              className="h-[40px] lg:h-auto text-[14px] lg:text-[16px] font-light cursor-pointer"
              asChild
            >
              <Link href="/services">
              {t("ourServices")}
              </Link>
            </Button>
          </div>
        </motion.div>

        <motion.hr
          className="border-[#353353] mt-[45px] mb-[30px] md:mt-[60px] md:mb-[36px] lg:mt-[70px] lg:mb-[40px] relative z-[1]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 0.7,
          }}
        />

        <motion.div
          className="grid grid-cols-2 md:grid-cols-[minmax(140px,170px)_minmax(160px,200px)_minmax(0,1fr)] lg:grid-cols-[minmax(200px,310px)_auto_auto_minmax(240px,340px)] lg:justify-between md:items-start gap-y-[56px] lg:gap-y-[40px] gap-x-[20px] md:gap-x-[40px] lg:gap-x-[40px] relative z-[1]"
          initial={{ y: 30 }}
          animate={{ y: 0 }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 0.9,
          }}
        >
          <div className="gap-y-[20px] flex flex-col gap-[20px] col-span-2 md:col-span-3 lg:col-span-1 lg:max-w-[310px] lg:self-stretch">
            <div className="text-[14px] lg:text-[16px] leading-[1.6] max-w-[290px] space-y-[20px] font-normal text-foreground">
              <Image
                src={"/assets/brand/footer-logo.svg"}
                alt="Flyingcode"
                width={243}
                height={40}
                className="h-[35px] object-contain object-[left_center] lg:h-auto"
              />
              <div>
                {typeof tagline === "string" ? parse(tagline) : ""}
              </div>
            </div>

            {/* SOCIALS */}
            <Socials alignment="left" className="mt-auto" feedUrl={locale === "en" ? "/feed.xml" : `/${locale}/feed.xml`} euGdprLabel={t("euGdprBadge")} />
          </div>
          <div className="space-y-[20px] md:col-span-1 lg:w-full lg:pt-[62px]">
            <h3 className="text-[14px] font-semibold leading-[1.5]">
              {t("quickLinks")}
            </h3>
            <ul className="text-[16px] font-normal leading-[1.5] space-y-[20px]">
              {[
                { href: "/", label: tNav("home") },
                { href: "/about-us", label: tNav("aboutUs") },
                { href: "/services", label: tNav("services") },
                { href: "/integrations", label: tNav("integrations") },
                { href: "/blog", label: tNav("blog") },
              ].map(({ href, label }) => (
                <li key={href}>
                  <Link className={footerLinkClass} href={href}>
                    {isActive(href) && <span className="inline-block w-[5px] h-[5px] rounded-full bg-theme mr-[8px] align-middle" />}
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-[20px] md:col-span-1 lg:w-full lg:pt-[62px]">
            <h3 className="text-[14px] font-semibold leading-[1.5]">
              {t("legalPages")}
            </h3>
            <ul className="text-[16px] font-normal leading-[1.5] space-y-[20px]">
              {[
                { href: "/privacy-policy", label: t("privacyPolicy") },
                { href: "/terms-of-service", label: t("termsOfService") },
                { href: "/cookie-policy", label: t("cookiePolicy") },
                { href: "/accessibility-statement", label: t("accessibilityStatement") },
                ...(locale === "de" ? [{ href: "/imprint", label: t("imprint") }] : []),
              ].map(({ href, label }) => (
                <li key={href}>
                  <Link className={footerLinkClass} href={href}>
                    {isActive(href) && <span className="inline-block w-[5px] h-[5px] rounded-full bg-theme mr-[8px] align-middle" />}
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-[20px] col-span-2 md:col-span-1 md:min-w-0 md:w-full md:max-w-none lg:w-full lg:max-w-[340px] lg:justify-self-end lg:pt-[62px]">
            <h3 className="text-[14px] font-semibold leading-[1.5]">Contact</h3>
            <ul className="text-[14px] font-normal leading-[1.5] space-y-[10px] md:space-y-[14px]">
              <li>
                <div className="block w-full">
                  <AnimatedCard className="w-full rounded-[10px] bg-[#121124] p-0 overflow-hidden">
                    <BorderWrapper
                      wrapperClass="rounded-[10px]"
                      className="rounded-[10px] min-h-[60px] bg-[#121124] p-[15px] flex items-stretch flex-row"
                    >
                      <span className="inline-flex items-center border-r border-white/10 w-[35px] min-w-[35px] mr-[15px]">
                        <Icons.mapMarker className="w-[16px] h-[20px]" />
                      </span>
                      <span className="min-w-0 flex-1 break-words leading-[1.6] flex items-center">
                        {t("location")}
                      </span>
                    </BorderWrapper>
                  </AnimatedCard>
                </div>
              </li>
              <li>
                <Link className="block w-full" href={"mailto:hello@flyingcode.eu"}>
                  <AnimatedCard className="w-full rounded-[10px] bg-[#121124] p-0 overflow-hidden">
                    <BorderWrapper
                      wrapperClass="rounded-[10px]"
                      className="rounded-[10px] min-h-[60px] bg-[#121124] p-[15px] flex items-stretch flex-row"
                    >
                      <span className="inline-flex items-center border-r border-white/10 w-[35px] min-w-[35px] mr-[15px]">
                        <Icons.email className="w-[16px] h-[20px]" />
                      </span>
                      <span className="min-w-0 flex-1 break-all leading-[1.6] flex items-center">
                        hello@flyingcode.eu
                      </span>
                    </BorderWrapper>
                  </AnimatedCard>
                </Link>
              </li>
              <li>
                <Link className="block w-full" href={"https://wa.me/447537133134"} target="_blank" rel="noopener noreferrer">
                  <AnimatedCard className="w-full rounded-[10px] bg-[#121124] p-0 overflow-hidden">
                    <BorderWrapper
                      wrapperClass="rounded-[10px]"
                      className="rounded-[10px] min-h-[60px] bg-[#121124] p-[15px] flex items-stretch flex-row"
                    >
                      <span className="inline-flex items-center border-r border-white/10 w-[35px] min-w-[35px] mr-[15px]">
                        <Icons.whatsapp className="w-[16px] h-[20px]" />
                      </span>
                      <span className="min-w-0 flex-1 break-words leading-[1.6] flex flex-col justify-center">
                        <span>+44 7537 133134</span>
                        <span className="text-[11px]">{t("whatsappOnly")}</span>
                      </span>
                    </BorderWrapper>
                  </AnimatedCard>
                </Link>
              </li>
            </ul>
          </div>
        </motion.div>

        <motion.hr
          className="border-[#353353] mt-[30px] mb-[20px] md:mt-[36px] md:mb-[24px] lg:mt-[40px] lg:mb-[28px] relative z-[1]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 1.1,
          }}
        />

        <motion.div
          className="flex flex-col-reverse md:flex-row flex-wrap pb-[40px] lg:pb-[80px] gap-y-[30px] gap-x-[20px] xl:gap-[60px] items-center justify-between md:justify-center relative z-[1]"
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 1.3,
          }}
        >
          <div className="text-[14px] font-normal leading-[1.5] text-foreground">
            © {new Date().getFullYear()} Flyingcode™ - {t("copyright")}
          </div>
        </motion.div>
      </div>
      <GetInTouch open={isGetInTouchOpen} onOpenChange={setIsGetInTouchOpen} />
    </footer>
  );
};

export default Footer;
